/**
 * Mock Data for Ticket to Luxury
 *
 * Sample events, packages, and testimonials for the luxury concert platform
 */

export interface Event {
  id: string;
  artistName: string;
  eventTitle: string;
  date: string;
  venue: string;
  city: string;
  country: 'Bangladesh' | 'India' | 'Nepal' | 'Sri Lanka';
  region: 'South Asia';
  description: string;
  imageUrl: string;
  ticketsRemaining: number;
  totalTickets: number;
  featured: boolean;
  packages: Package[];
}

export interface Package {
  id: string;
  eventId: string;
  tier: 'Diamond' | 'Platinum' | 'Gold';
  name: string;
  priceUSDT: number;
  perks: string[];
  seatingZone: string;
  availability: number;
  maxGuests: number;
}

export interface Testimonial {
  id: string;
  customerName: string;
  eventAttended: string;
  rating: number;
  review: string;
  avatarUrl: string;
  location: string;
  date: string;
}

export const mockEvents: Event[] = [
  {
    id: 'evt-001',
    artistName: 'Ed Sheeran',
    eventTitle: 'Mathematics World Tour - Dhaka',
    date: '2026-03-15',
    venue: 'Bangabandhu National Stadium',
    city: 'Dhaka',
    country: 'Bangladesh',
    region: 'South Asia',
    description: 'Experience Ed Sheeran live in an intimate stadium performance. VIP packages include exclusive backstage access, premium dining, and meet-and-greet opportunities.',
    imageUrl: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80',
    ticketsRemaining: 8,
    totalTickets: 50,
    featured: true,
    packages: [
      {
        id: 'pkg-001-diamond',
        eventId: 'evt-001',
        tier: 'Diamond',
        name: 'Ultimate VIP Experience',
        priceUSDT: 2500,
        perks: [
          'Front row premium seating',
          'Private backstage meet & greet',
          '5-star pre-concert dinner',
          'Exclusive merchandise package',
          'Dedicated concierge service',
          'Champagne & premium bar access',
          'Professional photo with artist',
          'VIP parking & lounge access'
        ],
        seatingZone: 'Front Row Center',
        availability: 3,
        maxGuests: 2
      },
      {
        id: 'pkg-001-platinum',
        eventId: 'evt-001',
        tier: 'Platinum',
        name: 'Premium VIP Package',
        priceUSDT: 1500,
        perks: [
          'Premium front section seating',
          'VIP lounge access',
          'Gourmet dinner buffet',
          'Exclusive merchandise',
          'Premium bar access',
          'Priority entry & exit',
          'Dedicated host service'
        ],
        seatingZone: 'Premium Front Section',
        availability: 5,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-002',
    artistName: 'Coldplay',
    eventTitle: 'Music of the Spheres - Mumbai',
    date: '2026-04-22',
    venue: 'DY Patil Stadium',
    city: 'Mumbai',
    country: 'India',
    region: 'South Asia',
    description: 'Coldplay returns to India with their spectacular Music of the Spheres tour. Immerse yourself in a visual and musical extravaganza with exclusive VIP amenities.',
    imageUrl: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=1920&q=80',
    ticketsRemaining: 15,
    totalTickets: 80,
    featured: true,
    packages: [
      {
        id: 'pkg-002-diamond',
        eventId: 'evt-002',
        tier: 'Diamond',
        name: 'Spheres Elite Experience',
        priceUSDT: 3000,
        perks: [
          'Front row center stage seating',
          'Backstage tour & meet & greet',
          'Michelin-star chef dining experience',
          'Premium gift box & signed merchandise',
          'Personal photographer',
          'Luxury transportation to/from venue',
          'Private VIP suite access',
          'Unlimited premium beverages'
        ],
        seatingZone: 'Stage Front Center',
        availability: 5,
        maxGuests: 2
      },
      {
        id: 'pkg-002-platinum',
        eventId: 'evt-002',
        tier: 'Platinum',
        name: 'Premium Galaxy Package',
        priceUSDT: 1800,
        perks: [
          'Premium reserved seating',
          'VIP lounge & bar access',
          'Pre-show dinner experience',
          'Exclusive merchandise',
          'Fast-track entry',
          'Dedicated VIP host',
          'Premium parking'
        ],
        seatingZone: 'Premium Reserved Section',
        availability: 10,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-003',
    artistName: 'Taylor Swift',
    eventTitle: 'The Eras Tour - Colombo',
    date: '2026-05-10',
    venue: 'Sugathadasa Stadium',
    city: 'Colombo',
    country: 'Sri Lanka',
    region: 'South Asia',
    description: 'Taylor Swift brings The Eras Tour to Sri Lanka for the first time. Experience three hours of hits spanning her entire career with unparalleled VIP treatment.',
    imageUrl: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=1920&q=80',
    ticketsRemaining: 6,
    totalTickets: 40,
    featured: true,
    packages: [
      {
        id: 'pkg-003-diamond',
        eventId: 'evt-003',
        tier: 'Diamond',
        name: 'Eras Ultimate VIP',
        priceUSDT: 3500,
        perks: [
          'Front row guaranteed seating',
          'Exclusive backstage meet & greet',
          'Fine dining experience by celebrity chef',
          'Limited edition signed memorabilia',
          'Professional photoshoot with artist',
          'Luxury suite with private viewing',
          'Personal VIP concierge',
          'Premium transportation & accommodation'
        ],
        seatingZone: 'Front Row VIP',
        availability: 2,
        maxGuests: 2
      },
      {
        id: 'pkg-003-platinum',
        eventId: 'evt-003',
        tier: 'Platinum',
        name: 'Premium Eras Package',
        priceUSDT: 2000,
        perks: [
          'Premium floor seating',
          'VIP lounge access',
          'Gourmet pre-show dinner',
          'Exclusive merchandise package',
          'Premium bar service',
          'Priority venue access',
          'VIP host service'
        ],
        seatingZone: 'Premium Floor Section',
        availability: 4,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-004',
    artistName: 'The Weeknd',
    eventTitle: 'After Hours til Dawn - Kathmandu',
    date: '2026-06-18',
    venue: 'Dashrath Stadium',
    city: 'Kathmandu',
    country: 'Nepal',
    region: 'South Asia',
    description: 'The Weeknd makes history with his first performance in Nepal. Experience cutting-edge production and hits from After Hours and Dawn FM with exclusive VIP access.',
    imageUrl: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1920&q=80',
    ticketsRemaining: 12,
    totalTickets: 60,
    featured: true,
    packages: [
      {
        id: 'pkg-004-diamond',
        eventId: 'evt-004',
        tier: 'Diamond',
        name: 'After Hours Diamond',
        priceUSDT: 2800,
        perks: [
          'Stage-side VIP seating',
          'Backstage access & meet & greet',
          'Premium dining experience',
          'Signed memorabilia collection',
          'Private photo opportunity',
          'VIP suite with private bar',
          'Dedicated concierge team',
          'Luxury ground transportation'
        ],
        seatingZone: 'Stage Side VIP',
        availability: 4,
        maxGuests: 2
      },
      {
        id: 'pkg-004-platinum',
        eventId: 'evt-004',
        tier: 'Platinum',
        name: 'Dawn Premium Package',
        priceUSDT: 1600,
        perks: [
          'Premium reserved seating',
          'VIP lounge & amenities',
          'Pre-concert dinner service',
          'Exclusive merchandise',
          'Premium beverage access',
          'Fast-track entry',
          'VIP host service'
        ],
        seatingZone: 'Premium Reserved',
        availability: 8,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-005',
    artistName: 'Bruno Mars',
    eventTitle: '24K Magic Live - Dhaka',
    date: '2026-07-25',
    venue: 'Army Stadium',
    city: 'Dhaka',
    country: 'Bangladesh',
    region: 'South Asia',
    description: 'Bruno Mars brings his electrifying 24K Magic show to Bangladesh. Expect high-energy performances, incredible showmanship, and luxury VIP treatment.',
    imageUrl: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1920&q=80',
    ticketsRemaining: 18,
    totalTickets: 70,
    featured: false,
    packages: [
      {
        id: 'pkg-005-diamond',
        eventId: 'evt-005',
        tier: 'Diamond',
        name: '24K Diamond Experience',
        priceUSDT: 2600,
        perks: [
          'Front row premium seating',
          'Backstage meet & greet',
          'Luxury dinner service',
          'Signed merchandise collection',
          'Professional photo session',
          'VIP suite access',
          'Personal concierge',
          'Premium parking & transportation'
        ],
        seatingZone: 'Front Row Premium',
        availability: 6,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-006',
    artistName: 'Dua Lipa',
    eventTitle: 'Future Nostalgia Tour - Bangalore',
    date: '2026-08-14',
    venue: 'Kanteerava Stadium',
    city: 'Bangalore',
    country: 'India',
    region: 'South Asia',
    description: 'Dua Lipa brings her Future Nostalgia tour to India. Dance the night away with chart-topping hits and experience VIP luxury like never before.',
    imageUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1920&q=80',
    ticketsRemaining: 22,
    totalTickets: 90,
    featured: false,
    packages: [
      {
        id: 'pkg-006-platinum',
        eventId: 'evt-006',
        tier: 'Platinum',
        name: 'Nostalgia Premium VIP',
        priceUSDT: 1700,
        perks: [
          'Premium floor seating',
          'VIP lounge access',
          'Gourmet dinner buffet',
          'Exclusive merchandise',
          'Premium bar service',
          'Priority entry & exit',
          'VIP host service'
        ],
        seatingZone: 'Premium Floor',
        availability: 15,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-007',
    artistName: 'Billie Eilish',
    eventTitle: 'Happier Than Ever Tour - Mumbai',
    date: '2026-09-05',
    venue: 'MMRDA Grounds',
    city: 'Mumbai',
    country: 'India',
    region: 'South Asia',
    description: 'Billie Eilish returns to India with her Happier Than Ever world tour. Experience her unique artistry and atmospheric production with exclusive VIP packages.',
    imageUrl: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1920&q=80',
    ticketsRemaining: 9,
    totalTickets: 50,
    featured: false,
    packages: [
      {
        id: 'pkg-007-diamond',
        eventId: 'evt-007',
        tier: 'Diamond',
        name: 'Happier Diamond VIP',
        priceUSDT: 2900,
        perks: [
          'Front section VIP seating',
          'Backstage tour & meet & greet',
          'Fine dining experience',
          'Limited edition signed items',
          'Professional photo with artist',
          'Private VIP lounge',
          'Dedicated concierge service',
          'Premium transportation'
        ],
        seatingZone: 'Front VIP Section',
        availability: 3,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-008',
    artistName: 'Imagine Dragons',
    eventTitle: 'Mercury World Tour - Colombo',
    date: '2026-10-12',
    venue: 'Sugathadasa Stadium',
    city: 'Colombo',
    country: 'Sri Lanka',
    region: 'South Asia',
    description: 'Imagine Dragons deliver their explosive live show to Sri Lanka. Rock out to hits like Radioactive, Believer, and Thunder with premium VIP treatment.',
    imageUrl: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=1920&q=80',
    ticketsRemaining: 14,
    totalTickets: 65,
    featured: false,
    packages: [
      {
        id: 'pkg-008-platinum',
        eventId: 'evt-008',
        tier: 'Platinum',
        name: 'Mercury Platinum VIP',
        priceUSDT: 1550,
        perks: [
          'Premium seating section',
          'VIP lounge & bar access',
          'Pre-show dinner experience',
          'Exclusive merchandise',
          'Fast-track venue entry',
          'Dedicated VIP host',
          'Premium parking'
        ],
        seatingZone: 'Premium Section',
        availability: 10,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-009',
    artistName: 'Ed Sheeran',
    eventTitle: 'Mathematics World Tour - Mumbai',
    date: '2026-11-20',
    venue: 'Wankhede Stadium',
    city: 'Mumbai',
    country: 'India',
    region: 'South Asia',
    description: 'Ed Sheeran returns to India with his Mathematics World Tour. Experience an unforgettable evening of acoustic brilliance and chart-topping hits with exclusive VIP access.',
    imageUrl: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80',
    ticketsRemaining: 7,
    totalTickets: 45,
    featured: true,
    packages: [
      {
        id: 'pkg-009-diamond',
        eventId: 'evt-009',
        tier: 'Diamond',
        name: 'Mathematics Diamond VIP',
        priceUSDT: 2700,
        perks: [
          'Front row center seating',
          'Private backstage meet & greet',
          'Luxury pre-concert dining',
          'Signed guitar pick & setlist',
          'Professional photo opportunity',
          'VIP lounge with premium bar',
          'Dedicated concierge service',
          'Complimentary luxury transportation'
        ],
        seatingZone: 'Front Row Center',
        availability: 3,
        maxGuests: 2
      },
      {
        id: 'pkg-009-platinum',
        eventId: 'evt-009',
        tier: 'Platinum',
        name: 'Premium Mathematics Package',
        priceUSDT: 1650,
        perks: [
          'Premium floor seating',
          'VIP lounge access',
          'Gourmet dinner service',
          'Exclusive merchandise bundle',
          'Premium beverage access',
          'Priority entry',
          'VIP host service'
        ],
        seatingZone: 'Premium Floor',
        availability: 4,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-010',
    artistName: 'Dua Lipa',
    eventTitle: 'Future Nostalgia Tour - Colombo',
    date: '2026-12-08',
    venue: 'R. Premadasa Stadium',
    city: 'Colombo',
    country: 'Sri Lanka',
    region: 'South Asia',
    description: 'Global pop sensation Dua Lipa brings her electrifying Future Nostalgia tour to Sri Lanka. Dance to hits like Levitating, Don\'t Start Now, and more with unparalleled VIP luxury.',
    imageUrl: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1920&q=80',
    ticketsRemaining: 11,
    totalTickets: 55,
    featured: true,
    packages: [
      {
        id: 'pkg-010-diamond',
        eventId: 'evt-010',
        tier: 'Diamond',
        name: 'Future Nostalgia Ultimate',
        priceUSDT: 2850,
        perks: [
          'Front section VIP seating',
          'Exclusive backstage access',
          'Celebrity chef dining experience',
          'Limited edition signed merchandise',
          'Professional photo session',
          'Private VIP suite',
          'Personal concierge team',
          'Luxury transportation package'
        ],
        seatingZone: 'Front VIP Section',
        availability: 4,
        maxGuests: 2
      },
      {
        id: 'pkg-010-platinum',
        eventId: 'evt-010',
        tier: 'Platinum',
        name: 'Nostalgia Platinum VIP',
        priceUSDT: 1750,
        perks: [
          'Premium reserved seating',
          'VIP lounge & amenities',
          'Pre-show dinner experience',
          'Exclusive merchandise',
          'Premium bar service',
          'Fast-track entry & exit',
          'Dedicated VIP host'
        ],
        seatingZone: 'Premium Reserved',
        availability: 7,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-011',
    artistName: 'BTS',
    eventTitle: 'BTS World Tour - Bangalore',
    date: '2027-01-15',
    venue: 'Kanteerava Stadium',
    city: 'Bangalore',
    country: 'India',
    region: 'South Asia',
    description: 'K-pop phenomenon BTS makes their highly anticipated return to India. Experience the global superstars perform Dynamite, Butter, and more with exclusive VIP packages and fan experiences.',
    imageUrl: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=1920&q=80',
    ticketsRemaining: 5,
    totalTickets: 100,
    featured: true,
    packages: [
      {
        id: 'pkg-011-diamond',
        eventId: 'evt-011',
        tier: 'Diamond',
        name: 'BTS Diamond ARMY VIP',
        priceUSDT: 4500,
        perks: [
          'Soundcheck access & exclusive performance',
          'Front row guaranteed seating',
          'Private group photo opportunity',
          'Ultra-luxury pre-concert dinner',
          'Limited edition signed ARMY box',
          'BTS official lightstick & merchandise',
          'Private VIP lounge access',
          'Personal translator & concierge',
          'Luxury hotel & transportation'
        ],
        seatingZone: 'Front Row ARMY Zone',
        availability: 2,
        maxGuests: 2
      },
      {
        id: 'pkg-011-platinum',
        eventId: 'evt-011',
        tier: 'Platinum',
        name: 'ARMY Premium Package',
        priceUSDT: 2500,
        perks: [
          'Premium pit seating',
          'VIP ARMY lounge access',
          'Korean-fusion dinner experience',
          'Official merchandise bundle',
          'BTS lightstick included',
          'Premium bar & refreshments',
          'Priority entry & photo zones',
          'Bilingual VIP host service'
        ],
        seatingZone: 'Premium ARMY Pit',
        availability: 3,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-012',
    artistName: 'BLACKPINK',
    eventTitle: 'BORN PINK World Tour - Dhaka',
    date: '2027-02-22',
    venue: 'Bangabandhu National Stadium',
    city: 'Dhaka',
    country: 'Bangladesh',
    region: 'South Asia',
    description: 'BLACKPINK brings their record-breaking BORN PINK tour to Bangladesh for the first time. Witness Jennie, Jisoo, Rosé, and Lisa perform their biggest hits with spectacular production and exclusive VIP experiences.',
    imageUrl: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1920&q=80',
    ticketsRemaining: 4,
    totalTickets: 85,
    featured: true,
    packages: [
      {
        id: 'pkg-012-diamond',
        eventId: 'evt-012',
        tier: 'Diamond',
        name: 'BLACKPINK Diamond BLINK',
        priceUSDT: 4200,
        perks: [
          'Soundcheck party access',
          'Front row center seating',
          'Hi-touch event with members',
          'Michelin-star dining experience',
          'Signed BORN PINK album & photocard set',
          'Official lightstick & luxury merch box',
          'Private VIP suite with champagne',
          'Personal concierge & translator',
          'Premium transportation & parking'
        ],
        seatingZone: 'Front Row BLINK Zone',
        availability: 2,
        maxGuests: 2
      },
      {
        id: 'pkg-012-platinum',
        eventId: 'evt-012',
        tier: 'Platinum',
        name: 'BLINK Premium Experience',
        priceUSDT: 2300,
        perks: [
          'Premium floor seating',
          'BLINK VIP lounge access',
          'K-food gourmet dinner',
          'Official merchandise package',
          'BLACKPINK lightstick',
          'Premium beverage service',
          'Priority entry & photo zones',
          'Bilingual VIP host'
        ],
        seatingZone: 'Premium BLINK Floor',
        availability: 2,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-013',
    artistName: 'TWICE',
    eventTitle: 'Ready To Be World Tour - Kathmandu',
    date: '2027-03-18',
    venue: 'Dashrath Stadium',
    city: 'Kathmandu',
    country: 'Nepal',
    region: 'South Asia',
    description: 'K-pop girl group TWICE brings their Ready To Be tour to Nepal. Experience the nine-member powerhouse perform Feel Special, Fancy, and more with premium VIP access and ONCE-exclusive perks.',
    imageUrl: 'https://images.unsplash.com/photo-1499364615650-ec38552f4f34?w=1920&q=80',
    ticketsRemaining: 8,
    totalTickets: 60,
    featured: false,
    packages: [
      {
        id: 'pkg-013-diamond',
        eventId: 'evt-013',
        tier: 'Diamond',
        name: 'TWICE Diamond ONCE VIP',
        priceUSDT: 3800,
        perks: [
          'Soundcheck access',
          'Front section VIP seating',
          'Group photo opportunity',
          'Luxury dinner experience',
          'Signed album & photocard collection',
          'Official CANDYBONG lightstick',
          'VIP lounge with Korean snacks',
          'Personal concierge service',
          'Premium transportation'
        ],
        seatingZone: 'Front ONCE VIP',
        availability: 3,
        maxGuests: 2
      },
      {
        id: 'pkg-013-platinum',
        eventId: 'evt-013',
        tier: 'Platinum',
        name: 'ONCE Premium Package',
        priceUSDT: 2100,
        perks: [
          'Premium reserved seating',
          'ONCE VIP lounge',
          'Korean BBQ dinner service',
          'Official merchandise bundle',
          'CANDYBONG lightstick',
          'Premium refreshments',
          'Priority venue access',
          'VIP host service'
        ],
        seatingZone: 'Premium ONCE Section',
        availability: 5,
        maxGuests: 2
      }
    ]
  },
  {
    id: 'evt-014',
    artistName: 'Ed Sheeran',
    eventTitle: 'Mathematics World Tour - Kathmandu',
    date: '2027-04-10',
    venue: 'Dashrath Stadium',
    city: 'Kathmandu',
    country: 'Nepal',
    region: 'South Asia',
    description: 'Ed Sheeran brings his Mathematics World Tour to Nepal for an intimate stadium performance. Experience the singer-songwriter magic with hits spanning his entire career in a luxury VIP setting.',
    imageUrl: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80',
    ticketsRemaining: 6,
    totalTickets: 40,
    featured: false,
    packages: [
      {
        id: 'pkg-014-diamond',
        eventId: 'evt-014',
        tier: 'Diamond',
        name: 'Mathematics Elite VIP',
        priceUSDT: 2650,
        perks: [
          'Front row VIP seating',
          'Backstage meet & greet',
          'Fine dining experience',
          'Signed merchandise collection',
          'Professional photo with artist',
          'VIP lounge access',
          'Dedicated concierge',
          'Premium transportation'
        ],
        seatingZone: 'Front Row VIP',
        availability: 2,
        maxGuests: 2
      },
      {
        id: 'pkg-014-platinum',
        eventId: 'evt-014',
        tier: 'Platinum',
        name: 'Premium Tour Package',
        priceUSDT: 1600,
        perks: [
          'Premium floor seating',
          'VIP lounge access',
          'Gourmet dinner service',
          'Exclusive merchandise',
          'Premium bar access',
          'Priority entry',
          'VIP host service'
        ],
        seatingZone: 'Premium Floor',
        availability: 4,
        maxGuests: 2
      }
    ]
  }
];

export const mockTestimonials: Testimonial[] = [
  {
    id: 'test-001',
    customerName: 'Priya Sharma',
    eventAttended: 'Coldplay - Music of the Spheres, Mumbai',
    rating: 5,
    review: 'Absolutely incredible experience! The Diamond package was worth every penny. Meeting Coldplay backstage was a dream come true, and the dinner was exquisite. Ticket to Luxury truly delivers on their promise of VIP treatment.',
    avatarUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    location: 'Mumbai, India',
    date: '2025-11-15'
  },
  {
    id: 'test-002',
    customerName: 'Rajesh Patel',
    eventAttended: 'Ed Sheeran - Mathematics Tour, Dhaka',
    rating: 5,
    review: 'From start to finish, everything was perfect. The concierge service made us feel like royalty. Front row seats were amazing, and the backstage meet & greet was intimate and well-organized. Highly recommend!',
    avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
    location: 'Dhaka, Bangladesh',
    date: '2025-10-22'
  },
  {
    id: 'test-003',
    customerName: 'Anjali Fernando',
    eventAttended: 'Taylor Swift - The Eras Tour, Colombo',
    rating: 5,
    review: 'This was the best concert experience of my life! The VIP lounge, gourmet dinner, and premium seating made it unforgettable. Ticket to Luxury exceeded all expectations. Already booking my next event!',
    avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&q=80',
    location: 'Colombo, Sri Lanka',
    date: '2025-12-03'
  },
  {
    id: 'test-004',
    customerName: 'Arjun Thapa',
    eventAttended: 'The Weeknd - After Hours til Dawn, Kathmandu',
    rating: 5,
    review: 'Exceptional service from booking to the event itself. The Platinum package gave us incredible seats and the VIP amenities were top-notch. Professional staff, seamless experience. Worth every USDT!',
    avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80',
    location: 'Kathmandu, Nepal',
    date: '2025-09-18'
  },
  {
    id: 'test-005',
    customerName: 'Nisha Kapoor',
    eventAttended: 'Bruno Mars - 24K Magic Live, Dhaka',
    rating: 5,
    review: 'Luxury at its finest! The entire experience felt exclusive and premium. From the champagne service to the backstage access, every detail was perfect. Ticket to Luxury is the only way to experience concerts!',
    avatarUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80',
    location: 'Kolkata, India',
    date: '2025-08-25'
  },
  {
    id: 'test-006',
    customerName: 'Kamal Ibrahim',
    eventAttended: 'Dua Lipa - Future Nostalgia, Bangalore',
    rating: 5,
    review: 'The VIP treatment made this concert truly special. Great communication, smooth booking process, and the event exceeded expectations. The dinner was outstanding and our seats were perfect!',
    avatarUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&q=80',
    location: 'Bangalore, India',
    date: '2025-07-14'
  }
];

// Helper functions
export function getFeaturedEvents(): Event[] {
  return mockEvents.filter(event => event.featured);
}

export function getEventById(id: string): Event | undefined {
  return mockEvents.find(event => event.id === id);
}

export function getEventsByCountry(country: string): Event[] {
  return mockEvents.filter(event => event.country === country);
}

export function getUpcomingEvents(): Event[] {
  return mockEvents.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function getLowAvailabilityEvents(): Event[] {
  return mockEvents.filter(event => event.ticketsRemaining < 10);
}
